import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { Translations } from "ui/Scripts/source/models/translations";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ITaskEditItemTaskState } from "source/models/task/taskEditItemTaskState";
import { ITaskEditItemTaskPriority } from "source/models/task/taskEditItemTaskPriority";
import { TaskDynamicFormContainer } from "../../TaskDynamicFormContainer";
import { TaskDynamicForm } from "../../TaskDynamicForm/TaskDynamicForm";
import { UserBlocks } from "source/components/user/UserBlocks/UserBlocks";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { TaskStateListChip } from "../../TaskStateListChip";
import { ITaskHandler } from "source/models/task/taskHandler";
import { TaskHandlerType } from "ui/Scripts/source/models/enums";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { ListStateIconArchived } from "source/components/framework/List/ListStateIconArchived";

interface ITaskEditDialogGeneralViewReadOnlyProps {
    companyId: string;
    name: string;
    isArchived: boolean;
    taskStateId: string;
    taskPriorityId: string;
    unitId: string;
    createUser: IModifyUser;
    taskHandlers: ITaskHandler[];
    taskType: ITaskEditTypeItem;
    taskHeaderDataCols: ITaskDataRowCol[];
    taskStates: ITaskEditItemTaskState[];
    taskPriorities: ITaskEditItemTaskPriority[];
    units: IIdTitleNode[];
}

export const TaskEditDialogGeneralViewReadOnly = (props: ITaskEditDialogGeneralViewReadOnlyProps) => {
    const [taskState, setTaskState] = useState<ITaskEditItemTaskState>(null);
    const [taskPriority, setTaskPriority] = useState<ITaskEditItemTaskPriority>(null);
    const [unit, setUnit] = useState<IIdTitleNode>(null);
    const [handlers, setHandlers] = useState<ITaskHandler[]>([]);
    const [personsInCharge, setPersonsInCharge] = useState<ITaskHandler[]>([]);

    useEffect(() => {
        setTaskState(props.taskStates.find(i => i.id === props.taskStateId) ?? props.taskStates[0]);
        setTaskPriority(props.taskPriorities.find(i => i.id === props.taskPriorityId) ?? props.taskPriorities[0]);
        setUnit(props.unitId ? IdTitleNode.getById(props.units, props.unitId) : null);
    }, [props.taskStateId, props.taskPriorityId, props.unitId]);

    useEffect(() => {
        setHandlers(props.taskHandlers.filter(i => i.taskHandlerType === TaskHandlerType.Handler));
        setPersonsInCharge(props.taskHandlers.filter(i => i.taskHandlerType === TaskHandlerType.PersonInCharge));
    }, [props.taskHandlers]);

    return (
        <Box sx={{ maxHeight: "calc(100vh - 500px)", overflowY: "auto", overflowX: "hidden" }}>
            <GridRow sx={{ position: "relative" }}>
                <Grid item sm={unit ? 8 : 12}>
                    <GridRow>
                        <Grid item sm={3}>
                            <FormField title={Translations.TaskType} margin={0}>
                                {props.taskType?.code}
                            </FormField>
                        </Grid>
                        <Grid item sm={3}>
                            <FormField title={Translations.TaskState} margin={0}>
                                <TaskStateListChip
                                    icon={props.isArchived ? <ListStateIconArchived hideTooltip={true} /> : undefined}
                                    code={props.isArchived ? Translations.Archived : taskState?.code}
                                    color={props.isArchived ? "EBEBEB" : taskState?.color}
                                    fullWidth={true}
                                />
                            </FormField>
                        </Grid>
                        <Grid item sm={3}>
                            <FormField title={Translations.Priority} margin={0}>
                                {taskPriority?.code}
                            </FormField>
                        </Grid>
                        <Grid item sm={3}>
                            <FormField title={unit ? Translations.Unit : Translations.Visibility} margin={0}>
                                {unit ? unit.title : Translations.Private}
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.Title}>
                                {props.name}
                            </FormField>
                        </Grid>
                    </GridRow>
                </Grid>
                {!!unit &&
                    <Grid item sm={4}>
                        <UserBlocks
                            margin="0"
                            avatarOnly={true}
                            companyId={props.companyId}
                            isReadOnly={true}
                            title={Translations.PersonsInCharge}
                            users={personsInCharge}
                            onAddUsers={null}
                            onRemoveUsers={null}
                        />
                        <UserBlocks
                            avatarOnly={true}
                            companyId={props.companyId}
                            isReadOnly={true}
                            title={Translations.TaskHandlers}
                            noUsersMessage={Translations.AllUsersOfTheUnit}
                            noUsersMessageUseInReadOnly={true}
                            fixedUsers={handlers.length > 0 && props.createUser ? [props.createUser] : undefined}
                            users={handlers}
                            onAddUsers={null}
                            onRemoveUsers={null}
                        />
                    </Grid>
                }
            </GridRow>
            {props.taskHeaderDataCols.length > 0 &&
                <Box sx={{ marginTop: "1rem" }}>
                    <TaskDynamicFormContainer>
                        <TaskDynamicForm
                            isReadOnly={true}
                            taskTypeCols={props.taskType.headerCols}
                            taskDataRowCols={props.taskHeaderDataCols}
                            onRefreshCols={null}
                        />
                    </TaskDynamicFormContainer>
                </Box>
            }
        </Box>
    );
};
